/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* istanbul ignore file */
import * as Survey from "survey-react";
import { PersonRequest } from "@plinknz/tah-website-elements";

import { EventType, GrantPayload } from "../../config/forms";
import { collapseSurrogate } from "../../service/forms/config-generics/personal-details";

import { stringifyAddressFinder } from "../address";
import { getPersonalChanges } from "../svg/member";
import { createSVG, createText } from "../svg/svg";

import { TunaWanangaSourceData } from "./tuna-wananga-types";

function conditionDetails({
  condition,
  details,
}: {
  condition: "Āe" | "Kāo";
  details: string;
}) {
  return condition === "Āe" ? details : condition;
}

function generateSVG({
  member,
  data,
}: {
  member: PersonRequest;
  data: TunaWanangaSourceData;
}) {
  const personalLines = getPersonalChanges({
    ...data,
    member,
  });

  const {
    attendeeCondition,
    attendeeConditionDetails,
    attendeeAllergies,
    attendeeAllergyDetails,
    attendanceDetails,
    accommodation,
    mediaRelease,
    contact1Name,
    contact1Relationship,
    contact1Phone,
    contact2Name,
    contact2Relationship,
    contact2Phone,
    signed,
  } = data;

  const lines = [...personalLines];

  lines.push(
    { heading: "Hauora" },
    {
      label: "Medical Conditions",
      text: conditionDetails({
        condition: attendeeCondition,
        details: attendeeConditionDetails,
      }),
    },
    {
      label: "Allergies",
      text: conditionDetails({
        condition: attendeeAllergies,
        details: attendeeAllergyDetails,
      }),
    },
    { heading: "Attendance" },
    { label: "Events", text: attendanceDetails },
    { label: "Accommodation", text: accommodation },
    { label: "Media Release", text: mediaRelease },
    { heading: "Emergency Contact 1" },
    { label: "Name", text: contact1Name },
    { label: "Relationship", text: contact1Relationship },
    { label: "Phone", text: contact1Phone },
    { heading: "Emergency Contact 2" },
    { label: "Name", text: contact2Name },
    { label: "Relationship", text: contact2Relationship },
    { label: "Phone", text: contact2Phone }
  );

  let index = 2;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const genericData = data as any;
  while (genericData[`attendee${index - 1}Additional`] === "Āe") {
    const attendee = `attendee${index}`;
    lines.push(
      { heading: `Attendee ${index}` },
      { label: "Name", text: genericData[`${attendee}Name`] },
      { label: "DOB", text: genericData[`${attendee}DOB`] },
      { label: "Email", text: genericData[`${attendee}Email`] },
      { label: "Phone", text: genericData[`${attendee}Number`] },
      {
        label: "Address",
        text: stringifyAddressFinder(genericData[`${attendee}Address`]),
      },
      {
        label: "Medical Conditions",
        text: conditionDetails({
          condition: genericData[`${attendee}Condition`],
          details: genericData[`${attendee}ConditionDetails`],
        }),
      },
      {
        label: "Allergies",
        text: conditionDetails({
          condition: genericData[`${attendee}Allergies`],
          details: genericData[`${attendee}AllergyDetails`],
        }),
      }
    );
    index += 1;
  }

  lines.push({ src: signed });

  const svg = createSVG({
    lines,
    heading: "Tuna Wānanga Application",
  });

  const text = createText({
    lines,
    heading: "Tuna Wānanga Application",
  });

  return { svg, text };
}

export function transformTunaWanangaData(
  survey: Survey.ReactSurveyModel,
  member: PersonRequest
): GrantPayload {
  const surveyData = survey.data;
  const data = collapseSurrogate(surveyData);
  const now = new Date().getTime();
  const tenant = process.env.TAH_TENANT;
  const { svg, text } = generateSVG({ data, member });

  const payloadData: GrantPayload = {
    data: {
      event: {
        eventDescription: EventType.TunaWananga,
        eventDate: now,
      },
      interaction: {
        interaction: svg,
        interactionDate: now,
      },
      email: {
        name: `${data.firstName} ${data.surname}`,
        email: data.email,
        message: text,
      },
    },
    targetId: data.id || member.id,
    recaptcha: null,
    tenant,
  };

  return payloadData;
}
